import { requestTraceHeaders, readRequestTrace, isDebugEnabled, requestTraceSink } from './chunk-6IBIFMFP.js';
import { unwrapData } from './chunk-HIWBYL4O.js';
import { canonicalRung } from './chunk-INX63VAT.js';
import { normalizeAddress } from './chunk-STJDIADC.js';
import { toCanonicalChainId } from './chunk-WUQGV726.js';
import { brandClass } from './chunk-N7SZ66QO.js';

// src/graphql/client.ts
var GraphQLRequestError = class extends Error {
  constructor(message, errors = [], status = 0, correlationId) {
    super(message);
    this.errors = errors;
    this.status = status;
    this.correlationId = correlationId;
    this.name = "GraphQLRequestError";
  }
  errors;
  status;
  correlationId;
  static {
    brandClass(this, "graphql/GraphQLRequestError");
  }
};
var CORRELATION_ID_HEADERS = [
  "x-correlation-id",
  // AppSync's own id for this request — present on success and on failure.
  "x-amzn-RequestId",
  // X-Ray, when tracing is enabled on the API.
  "x-amzn-Trace-Id",
  // CloudFront, when the endpoint is fronted by it.
  "x-amz-cf-id",
  "x-request-id"
];
var CORRELATION_ID_EXTENSION_KEYS = [
  "correlationId",
  "correlation_id",
  "requestId",
  "request_id"
];
var OPAQUE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:=+_-]{7,127}$/;
function isOpaqueRequestId(value) {
  return typeof value === "string" && OPAQUE_ID_RE.test(value) && !/arn:/i.test(value);
}
function correlationIdFrom(response, errors) {
  for (const error of errors) {
    const extensions = error?.extensions;
    if (!extensions || typeof extensions !== "object") continue;
    for (const key of CORRELATION_ID_EXTENSION_KEYS) {
      const value = extensions[key];
      if (isOpaqueRequestId(value)) return value;
    }
  }
  const headers = response?.headers;
  if (!headers || typeof headers.get !== "function") return void 0;
  for (const name of CORRELATION_ID_HEADERS) {
    const value = headers.get(name);
    if (isOpaqueRequestId(value)) return value;
  }
  return void 0;
}
function isGraphQLRequestError(err) {
  return err instanceof GraphQLRequestError;
}
var DEFAULT_TIMEOUT_MS = 15e3;
var GraphQLClient = class {
  static {
    brandClass(this, "graphql/GraphQLClient");
  }
  config;
  doFetch;
  /** See {@link onError}. */
  errorListeners = /* @__PURE__ */ new Set();
  constructor(config) {
    this.config = config;
    const impl = config.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : void 0);
    if (!impl) throw new Error("GraphQLClient: no fetch implementation available");
    this.doFetch = impl.bind(globalThis);
  }
  /**
   * Be told about every request that fails, whoever made it. The GraphQL twin of
   * `ApiClient.onErrorEnvelope`, for the same reason: a plan refusal (`PERM003`)
   * means the host's copy of the plan is stale, and the read that met it sits
   * behind any of a dozen adapters — most of which fall back to REST and never
   * say. A listener that throws is skipped: an observer must not become the
   * request's failure. Returns an unsubscribe.
   */
  onError(listener) {
    this.errorListeners.add(listener);
    return () => {
      this.errorListeners.delete(listener);
    };
  }
  /**
   * Execute one GraphQL operation and return its typed `data`. Throws
   * {@link GraphQLRequestError} on any transport failure or GraphQL `errors[]`.
   * Caller cancellation preserves the signal's reason and is not a failure.
   */
  async request(query, variables, opts = {}) {
    try {
      return await this.send(query, variables, opts);
    } catch (err) {
      opts.signal?.throwIfAborted();
      if (err instanceof GraphQLRequestError) {
        for (const listener of [...this.errorListeners]) {
          try {
            listener(err);
          } catch {
          }
        }
      }
      throw err;
    }
  }
  async send(query, variables, opts) {
    opts.signal?.throwIfAborted();
    const controller = new AbortController();
    const timeoutMs = opts.timeoutMs ?? this.config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const onAbort = () => controller.abort(opts.signal?.reason);
    let rejectCancelled;
    const cancelled = new Promise((_resolve, reject) => {
      rejectCancelled = () => reject(controller.signal.reason);
      controller.signal.addEventListener("abort", rejectCancelled, { once: true });
    });
    opts.signal?.addEventListener("abort", onAbort, { once: true });
    const timer = setTimeout(() => controller.abort(
      new GraphQLRequestError("GraphQL request timed out", [], 0)
    ), timeoutMs);
    try {
      return await Promise.race([
        this.sendOperation(query, variables, controller.signal),
        cancelled
      ]);
    } finally {
      clearTimeout(timer);
      opts.signal?.removeEventListener("abort", onAbort);
      controller.signal.removeEventListener("abort", rejectCancelled);
    }
  }
  async sendOperation(query, variables, signal) {
    signal.throwIfAborted();
    const endpoint = typeof this.config.endpoint === "function" ? this.config.endpoint() : this.config.endpoint;
    if (!endpoint) {
      throw new GraphQLRequestError("GraphQL endpoint is not resolvable", [], 0);
    }
    const token = await Promise.resolve(this.config.getAccessToken());
    signal.throwIfAborted();
    const headers = {
      ...this.config.requestTracingHeaders ? requestTraceHeaders() : {},
      "Content-Type": "application/json",
      Accept: "application/json"
    };
    if (token) headers.Authorization = token;
    if (this.config.requestSigner) {
      Object.assign(headers, await Promise.resolve(this.config.requestSigner()));
    }
    signal.throwIfAborted();
    const startedAt = Date.now();
    let response;
    try {
      response = await this.doFetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({ query, variables: variables ?? {} }),
        signal
      });
    } catch (err) {
      signal.throwIfAborted();
      throw new GraphQLRequestError(
        err instanceof Error ? err.message : "GraphQL request failed",
        [],
        0
      );
    }
    const trace = readRequestTrace(response, headers);
    if (isDebugEnabled()) requestTraceSink.capture({ url: endpoint, status: response.status, durationMs: Date.now() - startedAt, trace });
    signal.throwIfAborted();
    if (!response.ok) {
      const body2 = await safeJson(response);
      signal.throwIfAborted();
      const errors2 = extractErrors(body2);
      throw new GraphQLRequestError(
        errors2[0]?.message ?? `GraphQL request failed with status ${response.status}`,
        errors2,
        response.status,
        correlationIdFrom(response, errors2) ?? trace.correlation_id
      );
    }
    const body = await safeJson(response);
    signal.throwIfAborted();
    const errors = extractErrors(body);
    const firstError = errors[0];
    if (firstError) {
      throw new GraphQLRequestError(
        firstError.message,
        errors,
        response.status,
        correlationIdFrom(response, errors) ?? trace.correlation_id
      );
    }
    if (!body || body.data == null) {
      throw new GraphQLRequestError(
        "GraphQL response had no data",
        [],
        response.status,
        correlationIdFrom(response, []) ?? trace.correlation_id
      );
    }
    return body.data;
  }
};
async function safeJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
function extractErrors(body) {
  if (body && typeof body === "object" && Array.isArray(body.errors)) {
    return body.errors;
  }
  return [];
}

// src/graphql/awsJson.ts
function awsJsonArray(value) {
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return Array.isArray(parsed) ? parsed : null;
}

// src/graphql/operations/catalog.ts
var KIND_TO_GRAPHQL = {
  tag: "TAG",
  label: "LABEL",
  entity: "ENTITY",
  group: "GROUP"
};
function catalogKindToGraphql(kind) {
  return KIND_TO_GRAPHQL[kind];
}
var CATALOG_ITEM_FIELDS = `
  id
  createdBy
  createdAt
  updatedBy
  updatedAt
  kind
  name
  description
  color
  icon
  logo
  category
  entityType
  website
  twitter
  riskLevel
  severity
  lifecycle
  confidence
  confidenceStatus
  explanation
  systemDefault
  aliases
  address
  chain
  chains
  primaryChains
  primaryChainHistory
  groupType
  sagaKind
  state
  memberCount
  withheldByPolicy
  rung
  editable
  presentationOverride
`;
var CATALOG_ITEMS_QUERY = (
  /* GraphQL */
  `
  query CatalogItems(
    $kind: CatalogKind!
    $limit: Int
    $includeDefaults: Boolean
    $search: String
    $cursor: String
  ) {
    catalogItems(
      kind: $kind
      limit: $limit
      includeDefaults: $includeDefaults
      search: $search
      cursor: $cursor
    ) {
      items { ${CATALOG_ITEM_FIELDS} }
      authorableRungs
      nextCursor
      hasMore
      approxTotal
      approxTotalCapped
      confidencePolicy { minConfidence includeUnscored confidenceReview active }
    }
  }
`
);
var clean = (v) => v == null ? void 0 : v;
function mapCatalogItemToRow(item) {
  return {
    id: item.id,
    created_by: clean(item.createdBy),
    created_at: clean(item.createdAt),
    updated_by: clean(item.updatedBy),
    updated_at: clean(item.updatedAt),
    name: clean(item.name),
    description: clean(item.description),
    color: clean(item.color),
    // tag/label/group carry the logo under `icon`; entity/address under
    // `logo_s3_uri` — the unified GraphQL `logo` feeds the latter.
    icon: clean(item.icon),
    logo_s3_uri: clean(item.logo),
    category: clean(item.category),
    // The entity's identity, under the snake_case names `rowToDraft` reads. Without
    // these the editor drafts an entity with the type, site and handle blank — and
    // `draftToRowBody` then omits from the save what the form never showed. The
    // fields are all `rowToDraft` looks at, so this is the whole fix client-side.
    entity_type: clean(item.entityType),
    website: clean(item.website),
    twitter: clean(item.twitter),
    risk_level: clean(item.riskLevel),
    severity: clean(item.severity),
    lifecycle: clean(item.lifecycle),
    confidence: clean(item.confidence),
    confidence_status: clean(item.confidenceStatus),
    withheld_by_policy: clean(item.withheldByPolicy),
    explanation: item.explanation ?? [],
    system_default: item.systemDefault,
    aliases: item.aliases ?? [],
    // Address-scoped rows (address labels) carry their binding; the REST list
    // returns these too, so the transport-swapped row stays shape-identical.
    address: clean(item.address),
    chain: clean(item.chain),
    // Applicability scope so the list chip ("Applies to chains" / "All chains")
    // survives a refetch. The REST list carries this as `applies_to_chains`; the
    // adapter normalises it onto the same `chains` field so both transports emit
    // a shape-identical row.
    chains: item.chains ?? [],
    // Presentation hint so the editor re-shows a saved "Primary chains" pick
    // after a reload (REST returns `primary_chains` directly; `rowToDraft` reads
    // this snake_case field).
    primary_chains: item.primaryChains ?? [],
    primary_chain_history: awsJsonArray(item.primaryChainHistory),
    // The merged group view. A cross-chain saga the grouping engine found reaches
    // the list beside the groups a person curated, and `group_type` is how the
    // row renders as one rather than as a curated group with a broken name. The
    // saga fields are what the badge, leg count and state chip read; REST returns
    // them under exactly these names, so a transport swap changes nothing.
    group_type: clean(item.groupType),
    saga_kind: clean(item.sagaKind),
    state: clean(item.state),
    member_count: clean(item.memberCount),
    // The REST dump's names and spelling (a lower-case rung), so ownership reads
    // one shape off either transport and neither the badge nor the Edit gate
    // depends on which one won.
    rung: clean(item.rung?.toLowerCase()),
    editable: item.editable ?? void 0,
    presentation_override: awsJsonObject(item.presentationOverride)
  };
}
function awsJsonObject(value) {
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return void 0;
    }
  }
  return parsed != null && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : void 0;
}
function mapCatalogItems(data) {
  const page = data.catalogItems;
  return {
    items: (page?.items ?? []).map(mapCatalogItemToRow),
    authorableRungs: page?.authorableRungs?.map((r) => r.toLowerCase()) ?? void 0,
    nextCursor: page?.nextCursor ?? void 0,
    hasMore: page?.hasMore ?? void 0,
    total: page?.approxTotal ?? void 0,
    totalCapped: page?.approxTotalCapped ?? void 0,
    confidencePolicy: page?.confidencePolicy ? {
      min_confidence: page.confidencePolicy.minConfidence,
      include_unscored: page.confidencePolicy.includeUnscored,
      confidence_review: page.confidencePolicy.confidenceReview,
      active: page.confidencePolicy.active
    } : void 0
  };
}
function catalogListUnroutable(kind, query = {}) {
  if (query.minConfidence !== void 0 || query.includeUnscored !== void 0 || query.confidenceReview !== void 0) return "Confidence filters use REST";
  if (catalogKindToGraphql(kind) == null) return `kind "${kind}"`;
  if (query.chain) return "chain filter";
  if (query.lifecycle) return "lifecycle filter";
  if (query.groupType) return "group-type filter";
  if (query.sagaKind) return "saga-kind filter";
  return null;
}
async function fetchCatalogItems(client, kind, query = {}, opts) {
  const graphqlKind = catalogKindToGraphql(kind);
  if (!graphqlKind) throw new Error(`catalog kind "${kind}" is not served by GraphQL`);
  const data = await client.request(
    CATALOG_ITEMS_QUERY,
    {
      kind: graphqlKind,
      limit: query.limit,
      includeDefaults: query.includeDefaults ?? true,
      search: query.search,
      cursor: query.cursor
    },
    opts
  );
  return mapCatalogItems(data);
}
var GRAPHQL_CATALOG_ID_LIMIT = 1;
var CATALOG_ITEM_QUERY = (
  /* GraphQL */
  `
  query CatalogItem($kind: CatalogKind!, $id: ID!) {
    catalogItem(kind: $kind, id: $id) { ${CATALOG_ITEM_FIELDS} }
  }
`
);
async function fetchCatalogItem(client, kind, id, opts) {
  const graphqlKind = catalogKindToGraphql(kind);
  if (!graphqlKind) throw new Error(`catalog kind "${kind}" is not served by GraphQL`);
  const data = await client.request(
    CATALOG_ITEM_QUERY,
    { kind: graphqlKind, id },
    opts
  );
  return data.catalogItem ? mapCatalogItemToRow(data.catalogItem) : null;
}
function catalogItemsByIdRequest(kind, ids) {
  const declarations = ["$kind: CatalogKind!"];
  const fields = [];
  const variables = { kind };
  ids.forEach((id, i) => {
    declarations.push(`$i${i}: ID!`);
    variables[`i${i}`] = id;
    fields.push(`r${i}: catalogItem(kind: $kind, id: $i${i}) { ${CATALOG_ITEM_FIELDS} }`);
  });
  return {
    query: `query CatalogItemsById(${declarations.join(", ")}) {
${fields.join("\n")}
}`,
    variables
  };
}
async function graphqlFetchCatalogItemsByIds(client, kind, ids, opts) {
  if (ids.length === 0) return [];
  const graphqlKind = catalogKindToGraphql(kind);
  if (!graphqlKind) throw new Error(`catalog kind "${kind}" is not served by GraphQL`);
  if (ids.length > GRAPHQL_CATALOG_ID_LIMIT) {
    throw new RangeError(
      `graphqlFetchCatalogItemsByIds: ${ids.length} ids exceeds ${GRAPHQL_CATALOG_ID_LIMIT}; use REST`
    );
  }
  const { query, variables } = catalogItemsByIdRequest(graphqlKind, ids);
  const data = await client.request(
    query,
    variables,
    opts
  );
  const out = [];
  ids.forEach((_id, i) => {
    const item = data[`r${i}`];
    if (item) out.push(mapCatalogItemToRow(item));
  });
  return out;
}

// src/graphql/operations/related.ts
var RELATED_SEED_KINDS = [
  "tag",
  "label",
  "entity",
  "group",
  "transaction_group"
];
var CATALOG_RELATED_QUERY = (
  /* GraphQL */
  `
  query CatalogRelated($kind: RelatedKind!, $id: ID!, $depth: Int, $include: [RelatedKind!]) {
    catalogRelated(kind: $kind, id: $id, depth: $depth, include: $include) {
      seed {
        kind
        id
        chainId
      }
      nodes {
        ref {
          kind
          id
          chainId
        }
        name
        relation
        via {
          kind
          id
          chainId
        }
        depth
      }
      edges {
        source {
          kind
          id
          chainId
        }
        target {
          kind
          id
          chainId
        }
        relation
      }
      counts {
        kind
        count
      }
      depth
      truncated
      partial
    }
  }
`
);
var toEnum = (kind) => kind.toUpperCase();
var fromEnum = (value) => value.toLowerCase();
function mapRef(ref) {
  return {
    kind: fromEnum(ref.kind),
    id: ref.id,
    ...ref.chainId ? { chainId: ref.chainId } : {}
  };
}
function mapRelatedGraph(data) {
  const g = data.catalogRelated;
  if (!g) return null;
  const counts = {};
  for (const { kind, count } of g.counts ?? []) {
    counts[fromEnum(kind)] = count;
  }
  return {
    seed: mapRef(g.seed),
    nodes: (g.nodes ?? []).map((n) => ({
      ref: mapRef(n.ref),
      ...n.name ? { name: n.name } : {},
      ...n.relation ? { relation: fromEnum(n.relation) } : {},
      ...n.via ? { via: mapRef(n.via) } : {},
      depth: n.depth
    })),
    edges: (g.edges ?? []).map((e) => ({
      source: mapRef(e.source),
      target: mapRef(e.target),
      relation: fromEnum(e.relation)
    })),
    counts,
    depth: g.depth,
    truncated: g.truncated,
    partial: g.partial
  };
}
async function fetchCatalogRelated(client, kind, id, args = {}, opts) {
  const data = await client.request(
    CATALOG_RELATED_QUERY,
    {
      kind: toEnum(kind),
      id,
      depth: args.depth,
      include: args.include?.map(toEnum)
    },
    opts
  );
  return mapRelatedGraph(data);
}
function relatedChildren(graph, ref) {
  const byRelation = /* @__PURE__ */ new Map();
  for (const edge of graph.edges) {
    if (edge.source.kind !== ref.kind || edge.source.id !== ref.id) continue;
    const list = byRelation.get(edge.relation) ?? [];
    list.push(edge.target);
    byRelation.set(edge.relation, list);
  }
  return [...byRelation.entries()].map(([relation, targets]) => ({ relation, targets }));
}
function relatedOfKind(graph, kind) {
  return graph.nodes.filter((n) => n.ref.kind === kind && n.depth > 0);
}

// src/graphql/operations/address.ts
var ADDRESS_SCOPES = ["USER", "TENANT", "ORG", "CUSTOMER", "GLOBAL"];
function toAddressScope(scope) {
  const rung = canonicalRung(scope);
  if (!rung) return void 0;
  const upper = rung.toUpperCase();
  return ADDRESS_SCOPES.includes(upper) ? upper : void 0;
}
var RESOLVED_ENTITY_FIELDS = (
  /* GraphQL */
  `
  id
  name
  type
  logo
  color
  confidence
  confidenceStatus
  verified
`
);
var RESOLVED_HIT_CORE_FIELDS = (
  /* GraphQL */
  `
  kind
  catalogId
  name
  color
  logo
  tierWeight
  isPrimary
  severity
  riskLevel
  confidence
  confidenceStatus
  lifecycle
  explanation
`
);
var RESOLVED_HIT_FIELDS = (
  /* GraphQL */
  `
  ${RESOLVED_HIT_CORE_FIELDS}
  availableScopes {
    scope
    tierWeight
    overrides {
      key
      value
    }
  }
`
);
function resolvedBundleFields(hitFields) {
  return (
    /* GraphQL */
    `
    address
    chainId
    asOf
    entities { ${RESOLVED_ENTITY_FIELDS} }
    tags { ${hitFields} }
    labels { ${hitFields} }
    groups { ${hitFields} }
    primary {
      kind
      catalogId
    }
  `
  );
}
var ADDRESS_OVERLAYS_QUERY = (
  /* GraphQL */
  `
  query AddressOverlays($address: String!, $chainId: String, $asOf: Int, $scope: AddressScope) {
    address(address: $address, chainId: $chainId, asOf: $asOf, scope: $scope) {
      ${resolvedBundleFields(RESOLVED_HIT_FIELDS)}
    }
  }
`
);
var clean2 = (v) => v == null ? void 0 : v;
function mapScope(scope) {
  const overrides = {};
  for (const { key, value } of scope.overrides ?? []) overrides[key] = value;
  return { scope: scope.scope, tierWeight: scope.tierWeight, overrides };
}
function mapHit(hit) {
  return {
    kind: hit.kind,
    catalogId: hit.catalogId,
    name: clean2(hit.name),
    color: clean2(hit.color),
    logo: clean2(hit.logo),
    tierWeight: hit.tierWeight,
    isPrimary: hit.isPrimary,
    severity: clean2(hit.severity),
    riskLevel: clean2(hit.riskLevel),
    confidence: clean2(hit.confidence),
    confidence_status: clean2(hit.confidenceStatus),
    lifecycle: clean2(hit.lifecycle),
    explanation: hit.explanation ?? [],
    availableScopes: (hit.availableScopes ?? []).map(mapScope)
  };
}
function mapEntity(entity) {
  return {
    id: entity.id,
    name: clean2(entity.name),
    type: clean2(entity.type),
    logo: clean2(entity.logo),
    color: clean2(entity.color),
    confidence: clean2(entity.confidence),
    confidence_status: clean2(entity.confidenceStatus),
    verified: entity.verified ?? void 0
  };
}
function mapBundle(a) {
  return {
    address: a.address,
    chainId: clean2(a.chainId),
    asOf: a.asOf,
    entities: (a.entities ?? []).map(mapEntity),
    tags: (a.tags ?? []).map(mapHit),
    labels: (a.labels ?? []).map(mapHit),
    groups: (a.groups ?? []).map(mapHit),
    primary: a.primary ?? []
  };
}
function mapAddressOverlays(data) {
  return data.address ? mapBundle(data.address) : null;
}
async function fetchAddressOverlays(client, args, opts) {
  const data = await client.request(
    ADDRESS_OVERLAYS_QUERY,
    { address: args.address, chainId: args.chainId, asOf: args.asOf, scope: args.scope },
    opts
  );
  return mapAddressOverlays(data);
}

// src/graphql/operations/addressDetail.ts
var ADDRESS_ROW_FIELDS = `
  address
  chain
  addressType
  isPrimaryChain
  primaryChainHistory
  name
  notes
  symbol
  decimals
  isContract
  isContractVerified
  deployer
  deployedAt
  lastSeenAt
  riskLevel
  severity
  lifecycle
  isHumanApproved
  enrichmentStatus
  primaryEntityId
  logoS3Uri
  aliases
  source
  confidence
  systemDefault
  metadata
`;
var ADDRESS_DETAIL_QUERY = (
  /* GraphQL */
  `
  query AddressDetail($address: String!, $chainId: String!, $asOf: Int, $blockNumber: Float, $scope: AddressScope, $withScopes: Boolean) {
    addressDetail(address: $address, chainId: $chainId, asOf: $asOf, blockNumber: $blockNumber, scope: $scope, withScopes: $withScopes) {
      saved
      ownOverride
      ownFields
      row { ${ADDRESS_ROW_FIELDS} }
      otherScopes {
        scope
        readable
        count
        overrides { key value }
        fields
      }
    }
  }
`
);
function mapAddressRow(row) {
  return {
    address: row.address,
    chain: row.chain,
    address_type: row.addressType ?? void 0,
    is_primary_chain: Boolean(row.isPrimaryChain),
    primary_chain_history: awsJsonArray(row.primaryChainHistory),
    name: row.name ?? null,
    notes: row.notes ?? null,
    symbol: row.symbol ?? null,
    decimals: row.decimals ?? null,
    is_contract: row.isContract ?? null,
    is_contract_verified: row.isContractVerified ?? null,
    deployer: row.deployer ?? null,
    deployed_at: row.deployedAt ?? null,
    last_seen_at: row.lastSeenAt ?? null,
    risk_level: row.riskLevel ?? void 0,
    severity: row.severity ?? void 0,
    lifecycle: row.lifecycle ?? void 0,
    // The pair `isVerified`/`isBeingMapped` read. Projected here so the detail
    // panel judges a row the same way whichever mouth answered it.
    is_human_approved: Boolean(row.isHumanApproved),
    enrichment_status: row.enrichmentStatus ?? null,
    primary_entity_id: row.primaryEntityId ?? null,
    logo_s3_uri: row.logoS3Uri ?? null,
    aliases: row.aliases ?? [],
    source: row.source ?? null,
    confidence: row.confidence ?? void 0,
    system_default: Boolean(row.systemDefault),
    metadata: row.metadata ?? {}
  };
}
function mapAddressRowScope(scope) {
  const overrides = {};
  for (const { key, value } of scope.overrides ?? []) overrides[key] = value;
  return {
    scope: scope.scope,
    readable: Boolean(scope.readable),
    count: scope.count ?? 0,
    overrides,
    // Without these the edit dialog could not tell an inherited value from the
    // caller's own — the REST read always carried them, this read never did.
    fields: (scope.fields ?? []).filter((f) => typeof f === "string")
  };
}
function mapAddressDetail(data) {
  const d = data.addressDetail;
  if (!d) return null;
  return {
    address: mapAddressRow(d.row),
    saved: Boolean(d.saved),
    own_override: Boolean(d.ownOverride),
    own_fields: (d.ownFields ?? []).filter((f) => typeof f === "string"),
    // The catalog overlay bundle is fetched separately by the detail UI (the
    // `address` overlays op), so it is intentionally not selected here.
    catalog: void 0,
    other_scopes: (d.otherScopes ?? []).map(mapAddressRowScope)
  };
}
async function graphqlFetchAddressDetail(client, args, opts) {
  const data = await client.request(
    ADDRESS_DETAIL_QUERY,
    {
      address: args.address,
      chainId: args.chainId,
      asOf: args.asOf,
      blockNumber: args.blockNumber,
      scope: args.scope,
      withScopes: args.withScopes ?? false
    },
    opts
  );
  return mapAddressDetail(data);
}

// src/overlays/model.ts
var clean3 = (v) => v == null || v === "" ? void 0 : v;
function mapWireScope(scope) {
  return {
    scope: scope.scope,
    tierWeight: scope.tier_weight ?? 0,
    overrides: scope.overrides ?? {}
  };
}
function mapWireHit(hit) {
  return {
    kind: hit.kind,
    catalogId: hit.catalog_id,
    name: clean3(hit.name),
    color: clean3(hit.color),
    logo: clean3(hit.logo),
    tierWeight: hit.tier_weight ?? 0,
    isPrimary: Boolean(hit.is_primary),
    severity: clean3(hit.severity),
    riskLevel: clean3(hit.risk_level),
    confidence: hit.confidence ?? void 0,
    lifecycle: clean3(hit.lifecycle),
    explanation: hit.explanation ?? [],
    availableScopes: (hit.available_scopes ?? []).map(mapWireScope)
  };
}
function mapWireEntity(entity) {
  return {
    id: entity.id,
    name: clean3(entity.name),
    type: clean3(entity.type),
    logo: clean3(entity.logo),
    color: clean3(entity.color),
    confidence: entity.confidence ?? void 0,
    verified: entity.verified ?? void 0
  };
}
function catalogBlockToResolvedOverlays(block, ctx) {
  const b = block ?? {};
  const primary = Object.entries(b.primary ?? {}).map(([kind, catalogId]) => ({
    kind,
    catalogId: String(catalogId)
  }));
  return {
    address: ctx.address,
    chainId: ctx.chainId,
    asOf: ctx.asOf ?? Math.floor(Date.now() / 1e3),
    entities: (b.entities ?? []).map(mapWireEntity),
    tags: (b.tags ?? []).map(mapWireHit),
    labels: (b.labels ?? []).map(mapWireHit),
    groups: (b.groups ?? []).map(mapWireHit),
    primary
  };
}
function toResolvedOverlays(bundle) {
  return { ...bundle, entities: bundle.entities };
}
function isEmptyOverlays(bundle) {
  if (!bundle) return true;
  return bundle.entities.length === 0 && bundle.tags.length === 0 && bundle.labels.length === 0 && bundle.groups.length === 0;
}

// src/overlays/batch.ts
var RESOLVE_PATH = "/addresses/catalog/resolve";
function overlayRefKey(chainId, address, context) {
  const base = `${toCanonicalChainId(chainId) ?? chainId ?? ""}::${normalizeAddress(address, chainId)}`;
  return context?.asOf !== void 0 || context?.blockNumber !== void 0 ? `${base}@${context.asOf ?? ""}:${context.blockNumber ?? ""}` : base;
}
var EMPTY_BATCH_OVERLAYS = {
  byKey: /* @__PURE__ */ new Map(),
  byAddress: /* @__PURE__ */ new Map(),
  rowByKey: /* @__PURE__ */ new Map(),
  rowByAddress: /* @__PURE__ */ new Map(),
  savedKeys: /* @__PURE__ */ new Set(),
  unconfirmedKeys: /* @__PURE__ */ new Set()
};
function indexBatchEntries(entries) {
  const prepared = [];
  for (const entry of entries) {
    const ready = prepareBatchEntry(entry);
    if (ready) prepared.push(ready);
  }
  return indexPreparedEntries(prepared);
}
function prepareBatchEntry(entry) {
  if (!entry.address) return null;
  const addressKey = normalizeAddress(entry.address);
  const keys = /* @__PURE__ */ new Set([overlayRefKey(entry.chain, entry.address, entry)]);
  if (entry.askedChain !== void 0) keys.add(overlayRefKey(entry.askedChain, entry.address, entry));
  return { entry, keys: [...keys], addressKey };
}
function indexPreparedEntries(prepared) {
  const byKey = /* @__PURE__ */ new Map();
  const byAddress = /* @__PURE__ */ new Map();
  const rowByKey = /* @__PURE__ */ new Map();
  const rowByAddress = /* @__PURE__ */ new Map();
  const savedKeys = /* @__PURE__ */ new Set();
  const unconfirmedKeys = /* @__PURE__ */ new Set();
  for (const { entry, keys, addressKey } of prepared) {
    for (const key of keys) byKey.set(key, entry.overlays);
    if (entry.unconfirmed) for (const key of keys) unconfirmedKeys.add(key);
    if (entry.asOf === void 0 && entry.blockNumber === void 0) byAddress.set(addressKey, entry.overlays);
    if (entry.row) {
      for (const key of keys) rowByKey.set(key, entry.row);
      if (entry.asOf === void 0 && entry.blockNumber === void 0) rowByAddress.set(addressKey, entry.row);
      if (entry.saved) for (const key of keys) savedKeys.add(key);
    }
  }
  return { byKey, byAddress, rowByKey, rowByAddress, savedKeys, unconfirmedKeys };
}
function isOverlayAnswerConfirmed(batch, address, chainId, context) {
  if (!address || !lookupOverlays(batch, address, chainId, context)) return false;
  return !batch.unconfirmedKeys.has(overlayRefKey(chainId ?? null, address, context));
}
function isSavedAddressRow(batch, address, chainId, context) {
  if (!address) return false;
  return batch.savedKeys.has(overlayRefKey(chainId ?? null, address, context));
}
function lookupOverlays(batch, address, chainId, context) {
  if (!address) return void 0;
  if (!chainId && context?.asOf === void 0 && context?.blockNumber === void 0) return batch.byAddress.get(normalizeAddress(address));
  return batch.byKey.get(overlayRefKey(chainId, address, context));
}
function lookupAddressRow(batch, address, chainId, context) {
  if (!address) return void 0;
  if (!chainId && context?.asOf === void 0 && context?.blockNumber === void 0) return batch.rowByAddress.get(normalizeAddress(address));
  return batch.rowByKey.get(overlayRefKey(chainId, address, context));
}
function dedupeRefs(refs) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const ref of refs) {
    const address = (ref.address ?? "").trim();
    if (!address) continue;
    const key = overlayRefKey(ref.chainId, address, ref);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...ref, address, chainId: ref.chainId ?? null });
  }
  return out;
}
function refsCacheKey(refs) {
  return refs.map((r) => overlayRefKey(r.chainId, r.address, r)).sort().join(",");
}
var RESOLVE_REF_LIMIT = 500;
var RESOLVE_CONCURRENCY = 3;
async function resolveChunk(client, refs, opts) {
  const body = {
    refs: refs.map((r) => ({ chain: r.chainId ?? void 0, address: r.address, ...r.asOf !== void 0 ? { as_of: r.asOf } : {}, ...r.blockNumber !== void 0 ? { block_number: r.blockNumber } : {} })),
    ...opts?.includeRow ? { include_row: true } : {},
    ...opts?.proxyContext ? { proxy_context: opts.proxyContext } : {},
    ...opts?.withScopes ? { scopes: true } : {},
    // `USER` is the caller's own view, which is what omitting it already means.
    // Sending it anyway would be a distinct request body for an identical read.
    ...opts?.scope && opts.scope !== "USER" ? { scope: opts.scope } : {}
  };
  const res = unwrapData(
    await client.post(RESOLVE_PATH, body, { signal: opts?.signal })
  );
  return { entries: res?.resolved ?? [], marksDegraded: res?.marks_degraded === true };
}
function isEmptyCatalogBlock(block) {
  if (!block) return true;
  return !block.tags?.length && !block.labels?.length && !block.groups?.length && !block.entities?.length && Object.keys(block.primary ?? {}).length === 0;
}
function isUnconfirmedEntry(entry, marksDegraded, includeRow) {
  if (entry.catalog?.degraded === true) return true;
  if (!marksDegraded && isEmptyCatalogBlock(entry.catalog)) return true;
  return includeRow && !("row" in entry);
}
function sameChain(a, b) {
  return (toCanonicalChainId(a) ?? "") === (toCanonicalChainId(b) ?? "");
}
function askedChainFor(entry, asked) {
  const chains = asked.get(normalizeAddress(entry.address));
  if (!chains) return void 0;
  if (chains.length === 1) return chains[0];
  return chains.find((chain) => sameChain(chain, entry.chain));
}
async function fetchAddressOverlaysBatch(client, refs, opts) {
  const cleaned = dedupeRefs(refs);
  if (cleaned.length === 0) return EMPTY_BATCH_OVERLAYS;
  const chunks = [];
  for (let i = 0; i < cleaned.length; i += RESOLVE_REF_LIMIT) {
    chunks.push(cleaned.slice(i, i + RESOLVE_REF_LIMIT));
  }
  const entries = [];
  for (let i = 0; i < chunks.length; i += RESOLVE_CONCURRENCY) {
    const slice = chunks.slice(i, i + RESOLVE_CONCURRENCY);
    const wave = await Promise.all(slice.map((chunk) => resolveChunk(client, chunk, opts)));
    for (let c = 0; c < wave.length; c += 1) {
      const asked = /* @__PURE__ */ new Map();
      for (const ref of slice[c]) {
        const lowered = normalizeAddress(ref.address);
        asked.set(lowered, [...asked.get(lowered) ?? [], ref.chainId]);
      }
      const { entries: resolved, marksDegraded } = wave[c];
      for (const entry of resolved) {
        if (!entry?.address) continue;
        entries.push({
          address: entry.address,
          chain: entry.chain,
          asOf: entry.as_of,
          blockNumber: entry.block_number,
          askedChain: askedChainFor(entry, asked),
          overlays: catalogBlockToResolvedOverlays(entry.catalog ?? void 0, {
            address: entry.address,
            chainId: entry.chain ?? void 0
          }),
          row: entry.row,
          saved: entry.saved,
          unconfirmed: isUnconfirmedEntry(entry, marksDegraded, opts?.includeRow === true)
        });
      }
    }
  }
  return indexBatchEntries(entries);
}
async function resolveAddressAcrossChains(client, address, chains, opts) {
  const cleanAddress = (address ?? "").trim();
  const uniqueChains = Array.from(
    new Set(chains.map((c) => (c ?? "").trim()).filter(Boolean).map((c) => toCanonicalChainId(c) ?? c))
  );
  if (!cleanAddress || uniqueChains.length === 0) return [];
  const body = {
    refs: uniqueChains.map((chain) => ({ chain, address: cleanAddress })),
    include_row: true,
    ...opts?.withScopes ? { scopes: true } : {},
    ...opts?.scope && opts.scope !== "USER" ? { scope: opts.scope } : {}
  };
  const res = unwrapData(
    await client.post(RESOLVE_PATH, body, { signal: opts?.signal })
  );
  const entries = res?.resolved ?? [];
  const byRef = /* @__PURE__ */ new Map();
  entries.forEach((entry) => {
    if (entry?.chain && entry.address) byRef.set(overlayRefKey(entry.chain, entry.address), entry);
  });
  return uniqueChains.map((chain) => {
    const entry = byRef.get(overlayRefKey(chain, cleanAddress));
    return {
      chain,
      overlays: catalogBlockToResolvedOverlays(entry?.catalog ?? void 0, {
        address: cleanAddress,
        chainId: chain
      }),
      row: entry?.row ?? null,
      saved: Boolean(entry?.saved)
    };
  });
}
function boundOverlayRefs(refs, limit) {
  const unique = dedupeRefs(refs);
  const cap = Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : 0;
  const bounded = unique.slice(0, cap);
  return { refs: bounded, requestedCount: unique.length, queriedCount: bounded.length, limit: cap, truncated: unique.length > cap };
}

// src/graphql/operations/addressList.ts
var ADDRESSES_QUERY = (
  /* GraphQL */
  `
  query Addresses(
    $scope: AddressListScope
    $entityId: String
    $includeGlobal: Boolean
    $limit: Int
    $cursor: String
    $resolveScope: AddressScope
    $addressType: String
    $chainIds: [String!]
  ) {
    addresses(
      scope: $scope
      entityId: $entityId
      includeGlobal: $includeGlobal
      limit: $limit
      cursor: $cursor
      resolveScope: $resolveScope
      addressType: $addressType
      chainIds: $chainIds
    ) {
      items {
        row { ${ADDRESS_ROW_FIELDS} }
        catalog { ${resolvedBundleFields(RESOLVED_HIT_CORE_FIELDS)} }
      }
      count
      nextCursor
    }
  }
`
);
var LIST_MODE_ENUM = {
  tenant: "TENANT",
  entity: "ENTITY"
};
function graphqlListMode(mode) {
  return LIST_MODE_ENUM[mode ?? "tenant"] ?? null;
}
var ARGUMENT_DISPOSITION = {
  /** Translated to the SDL's enum; `cross_chain` has no member. */
  mode: "translated",
  entity_id: "sent",
  include_global: "sent",
  limit: "sent",
  cursor: "sent",
  address_type: "sent",
  chain_ids: "sent",
  /**
   * NO `address` argument exists on `addresses`. The single-address lookup is
   * server-side, answers completely and returns no cursor; a page of everything
   * is not a narrower version of that answer, it is the wrong one.
   */
  address: "unsupported"
};
var UNSUPPORTED_REASON = {
  address: "single-address search"
};
function stated(value) {
  if (value === void 0 || value === null || value === false) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value !== "";
  return true;
}
function addressListUnroutable(query = {}) {
  if (graphqlListMode(query.mode) === null) return `mode "${query.mode}"`;
  for (const [field, disposition] of Object.entries(ARGUMENT_DISPOSITION)) {
    if (disposition !== "unsupported") continue;
    const key = field;
    if (stated(query[key])) return UNSUPPORTED_REASON[key] ?? field;
  }
  return null;
}
function mapAddressPage(data) {
  const page = data.addresses;
  const addresses = (page?.items ?? []).map((item) => mapAddressRow(item.row));
  return {
    addresses,
    next_cursor: page?.nextCursor ?? null,
    // The SDL types `count` non-null, so the fallback is belt-and-braces — but a
    // 0 reported next to visible rows would make the table contradict its own
    // header, which is worth one `??`.
    count: page?.count ?? addresses.length
  };
}
function mapAddressListOverlays(data) {
  const entries = [];
  for (const item of data.addresses?.items ?? []) {
    if (!item.catalog) continue;
    const row = mapAddressRow(item.row);
    const address = item.catalog.address || row.address;
    if (!address) continue;
    entries.push({
      address,
      chain: item.catalog.chainId ?? row.chain,
      overlays: mapBundle(item.catalog),
      row,
      saved: true
    });
  }
  return indexBatchEntries(entries);
}
async function graphqlListAddresses(client, query = {}, opts) {
  return (await graphqlListAddressesWithOverlays(client, query, opts)).page;
}
async function graphqlListAddressesWithOverlays(client, query = {}, opts, resolveScope) {
  const unroutable = addressListUnroutable(query);
  if (unroutable) throw new Error(`address list: ${unroutable} has no GraphQL equivalent`);
  const scope = graphqlListMode(query.mode);
  const data = await client.request(
    ADDRESSES_QUERY,
    {
      scope,
      entityId: query.entity_id,
      includeGlobal: query.include_global,
      limit: query.limit,
      cursor: query.cursor,
      addressType: query.address_type || void 0,
      // Absent, not `[]`: an empty list reaches the server as a filter no chain
      // satisfies, which would answer "no addresses" for a cleared facet.
      chainIds: query.chain_ids?.length ? [...query.chain_ids] : void 0,
      // `USER` is the caller's own view, which is what omitting it already means.
      resolveScope: resolveScope && resolveScope !== "USER" ? resolveScope : void 0
    },
    opts
  );
  return { page: mapAddressPage(data), overlays: mapAddressListOverlays(data) };
}

// src/graphql/operations/addressCrossChain.ts
var ADDRESS_CROSS_CHAIN_QUERY = (
  /* GraphQL */
  `
  query AddressCrossChain($address: String!, $limit: Int, $resolveScope: AddressScope) {
    addressCrossChain(address: $address, limit: $limit, resolveScope: $resolveScope) {
      address
      count
      activity {
        address
        activeChainIds
        totalTxCount
        totalDefiCount
        totalNftCount
        totalTokenTransferCount
        firstSeenAt
        firstTxHash
        firstSeenChainId
        lastSeenAt
        lastTxHash
        lastSeenChainId
        busiestChainId
        refreshedAt
        chains
      }
      addresses {
        row { ${ADDRESS_ROW_FIELDS} }
        catalog { ${resolvedBundleFields(RESOLVED_HIT_CORE_FIELDS)} }
      }
    }
  }
`
);
function readJsonObject(value) {
  const parsed = typeof value === "string" ? safeParse(value) : value;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  return parsed;
}
function safeParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
function mapCrossChainActivity(activity) {
  if (!activity) return null;
  return {
    address: activity.address,
    active_chain_ids: activity.activeChainIds ?? [],
    chains: readJsonObject(activity.chains),
    total_tx_count: activity.totalTxCount ?? 0,
    total_defi_count: activity.totalDefiCount ?? 0,
    total_nft_count: activity.totalNftCount ?? 0,
    total_token_transfer_count: activity.totalTokenTransferCount ?? 0,
    first_seen_at: activity.firstSeenAt,
    first_tx_hash: activity.firstTxHash,
    first_seen_chain_id: activity.firstSeenChainId,
    last_seen_at: activity.lastSeenAt,
    last_tx_hash: activity.lastTxHash,
    last_seen_chain_id: activity.lastSeenChainId,
    busiest_chain_id: activity.busiestChainId ?? null,
    refreshed_at: activity.refreshedAt
  };
}
function mapAddressCrossChain(data) {
  const lens = data.addressCrossChain;
  const addresses = (lens?.addresses ?? []).map((item) => {
    const row = mapAddressRow(item.row);
    return {
      ...row,
      // The two the SDL has no field for — same defaults the REST adapter
      // applies to a row that arrives without them. See the module note.
      saved: true,
      annotated: false,
      // `enrichment_status` IS on `AddressRow`, so `isBeingMapped` answers the
      // same on both transports. `mapAddressRow` already carries it; named here
      // because it is the field the whole "is this row still being worked on"
      // affordance hangs off.
      enrichment_status: row.enrichment_status ?? null,
      catalog: item.catalog ?? void 0
    };
  });
  return {
    addresses,
    activity: mapCrossChainActivity(lens?.activity ?? null),
    // The server's own count, not the page's: the two differ when `limit` clips,
    // and reporting the clipped number as the total is how "this address is on
    // 3 chains" gets said about one that is on forty.
    count: lens?.count ?? addresses.length
  };
}
function addressCrossChainIncomplete(lens) {
  if (lens.activity == null) {
    return "no stored activity rollup; only the REST lens consults the activity providers";
  }
  return null;
}
async function graphqlFetchAddressCrossChain(client, address, args = {}, opts) {
  const data = await client.request(
    ADDRESS_CROSS_CHAIN_QUERY,
    {
      address,
      limit: args.limit,
      // `USER` is the caller's own view, which is what omitting it already means.
      resolveScope: args.resolveScope && args.resolveScope !== "USER" ? args.resolveScope : void 0
    },
    opts
  );
  return mapAddressCrossChain(data);
}

// src/graphql/operations/addressBatch.ts
var GRAPHQL_BATCH_REF_LIMIT = 50;
function addressBatchRequest(refs, opts = {}) {
  const bundle = resolvedBundleFields(opts.withScopes ? RESOLVED_HIT_FIELDS : RESOLVED_HIT_CORE_FIELDS);
  const variables = {
    scope: opts.scope && opts.scope !== "USER" ? opts.scope : void 0
  };
  const declarations = ["$scope: AddressScope"];
  const fields = [];
  const withRow = [];
  refs.forEach((ref, i) => {
    const chain = ref.chainId || void 0;
    const row = Boolean(opts.includeRow && chain);
    variables[`a${i}`] = ref.address;
    variables[`c${i}`] = chain;
    withRow.push(row);
    if (row) {
      declarations.push(`$a${i}: String!`, `$c${i}: String!`);
      fields.push(
        `r${i}: addressDetail(address: $a${i}, chainId: $c${i}, scope: $scope, withScopes: $withScopes) {
          saved
          row { ${ADDRESS_ROW_FIELDS} }
          catalog { ${bundle} }
        }`
      );
    } else {
      declarations.push(`$a${i}: String!`, `$c${i}: String`);
      fields.push(`r${i}: address(address: $a${i}, chainId: $c${i}, scope: $scope) { ${bundle} }`);
    }
  });
  if (withRow.some(Boolean)) {
    declarations.push("$withScopes: Boolean");
    variables.withScopes = Boolean(opts.withScopes);
  }
  return {
    query: `query AddressBatch(${declarations.join(", ")}) {
${fields.join("\n")}
}`,
    variables,
    withRow
  };
}
async function graphqlResolveAddressesBatch(client, refs, opts = {}, requestOpts) {
  const cleaned = dedupeRefs(refs);
  if (cleaned.length === 0) return EMPTY_BATCH_OVERLAYS;
  if (cleaned.length > GRAPHQL_BATCH_REF_LIMIT) {
    throw new RangeError(
      `graphqlResolveAddressesBatch: ${cleaned.length} refs exceeds ${GRAPHQL_BATCH_REF_LIMIT}; use the REST batch`
    );
  }
  const { query, variables, withRow } = addressBatchRequest(cleaned, opts);
  const data = await client.request(query, variables, requestOpts);
  const entries = cleaned.map((ref, i) => {
    const empty = () => catalogBlockToResolvedOverlays(void 0, { address: ref.address, chainId: ref.chainId ?? void 0 });
    if (withRow[i]) {
      const detail = data[`r${i}`] ?? null;
      return {
        address: ref.address,
        askedChain: ref.chainId,
        chain: detail?.catalog?.chainId ?? detail?.row?.chain ?? ref.chainId,
        overlays: detail?.catalog ? mapBundle(detail.catalog) : empty(),
        row: detail?.row ? mapAddressRow(detail.row) : null,
        saved: Boolean(detail?.saved)
      };
    }
    const bundle = data[`r${i}`] ?? null;
    return {
      address: ref.address,
      askedChain: ref.chainId,
      chain: bundle?.chainId ?? ref.chainId,
      overlays: bundle ? mapBundle(bundle) : empty()
    };
  });
  return indexBatchEntries(entries);
}

// src/graphql/operations/transactions.ts
var LATEST_TRANSACTIONS_QUERY = (
  /* GraphQL */
  `
  query LatestTransactions($limit: Int, $cursor: String) {
    latestTransactions(limit: $limit, cursor: $cursor) {
      items {
        canonicalTxId
        chainId
        blockTimestamp
        entities {
          id
          name
        }
        tags {
          catalogId
          name
          color
          logo
        }
        labels {
          catalogId
          name
          color
          logo
        }
        groups {
          catalogId
          name
          color
          logo
        }
      }
      nextCursor
    }
  }
`
);
var clean4 = (v) => v == null ? void 0 : v;
function mapRef2(ref) {
  return {
    catalogId: ref.catalogId,
    name: clean4(ref.name),
    color: clean4(ref.color),
    logo: clean4(ref.logo)
  };
}
function mapItem(item) {
  return {
    canonicalTxId: item.canonicalTxId,
    chainId: item.chainId,
    blockTimestamp: item.blockTimestamp,
    entities: (item.entities ?? []).map((e) => ({ id: e.id, name: clean4(e.name) })),
    tags: (item.tags ?? []).map(mapRef2),
    labels: (item.labels ?? []).map(mapRef2),
    groups: (item.groups ?? []).map(mapRef2)
  };
}
function mapLatestTransactions(data) {
  const feed = data.latestTransactions ?? { items: [], nextCursor: null };
  return {
    items: (feed.items ?? []).map(mapItem),
    nextCursor: clean4(feed.nextCursor)
  };
}
async function fetchLatestTransactions(client, args = {}, opts) {
  const data = await client.request(
    LATEST_TRANSACTIONS_QUERY,
    { limit: args.limit, cursor: args.cursor },
    opts
  );
  return mapLatestTransactions(data);
}

// src/graphql/operations/transaction.ts
var TRANSACTION_HEADER_QUERY = (
  /* GraphQL */
  `
  query TransactionHeader($canonicalTxId: ID, $chainId: String, $txHash: String) {
    transaction(canonicalTxId: $canonicalTxId, chainId: $chainId, txHash: $txHash) {
      canonicalTxId
      chainId
      txHash
      blockNumber
      blockHash
      blockTimestamp
      finality
      saved
      hasBody
      enrichmentStatus
      runId
      notes
    }
  }
`
);
var clean5 = (v) => v == null ? void 0 : v;
function mapTransactionHeader(data) {
  const t = data.transaction;
  if (!t) return null;
  return {
    canonicalTxId: t.canonicalTxId,
    chainId: t.chainId,
    txHash: clean5(t.txHash),
    blockNumber: clean5(t.blockNumber),
    blockHash: clean5(t.blockHash),
    blockTimestamp: clean5(t.blockTimestamp),
    finality: clean5(t.finality),
    saved: Boolean(t.saved),
    hasBody: Boolean(t.hasBody),
    enrichmentStatus: clean5(t.enrichmentStatus),
    runId: clean5(t.runId),
    notes: clean5(t.notes)
  };
}
async function fetchTransactionHeader(client, args, opts) {
  const data = await client.request(
    TRANSACTION_HEADER_QUERY,
    { canonicalTxId: args.canonicalTxId, chainId: args.chainId, txHash: args.txHash },
    opts
  );
  return mapTransactionHeader(data);
}
var REQUEST_TRANSACTION_MUTATION = (
  /* GraphQL */
  `
  mutation RequestTransaction($chainId: String!, $txHash: String!, $decode: Boolean) {
    requestTransaction(chainId: $chainId, txHash: $txHash, decode: $decode) {
      canonicalTxId
      chainId
      txHash
      blockNumber
      blockHash
      blockTimestamp
      finality
      saved
      hasBody
      enrichmentStatus
      runId
      notes
    }
  }
`
);
async function graphqlRequestTransaction(client, args, opts) {
  const data = await client.request(
    REQUEST_TRANSACTION_MUTATION,
    { chainId: args.chainId, txHash: args.txHash, decode: args.decode ?? false },
    opts
  );
  return mapTransactionHeader({ transaction: data.requestTransaction });
}

// src/graphql/operations/assignments.ts
var CATALOG_KIND_ENUM = {
  entity: "ENTITY",
  tag: "TAG",
  label: "LABEL",
  group: "GROUP"
};
function toCatalogKindEnum(kind) {
  const enumValue = CATALOG_KIND_ENUM[kind?.toLowerCase?.() ?? ""];
  if (!enumValue) throw new Error(`catalog kind "${kind}" is not a GraphQL CatalogKind`);
  return enumValue;
}
var clean6 = (v) => v == null ? void 0 : v;
function toMemberTier(raw) {
  const value = raw?.toLowerCase?.();
  return value === "core" || value === "derived" ? value : null;
}
function toTierEnum(tier) {
  return tier == null ? void 0 : tier.toUpperCase();
}
function mapAssignment(row) {
  return {
    id: row.id,
    scope: row.scope,
    validity_periods: typeof row.validityPeriods === "string" ? JSON.parse(row.validityPeriods) : row.validityPeriods ?? [],
    valid_from: row.validFrom ?? null,
    valid_to: row.validTo ?? null,
    scope_target_id: row.scopeTargetId,
    chain_id: row.chainId ?? null,
    catalog_kind: row.catalogKind,
    catalog_id: row.catalogId,
    user_id: row.userId ?? null,
    is_human_approved: row.isHumanApproved,
    created_at: clean6(row.createdAt),
    confidence: clean6(row.confidence),
    confidence_status: clean6(row.confidenceStatus),
    explanation: row.explanation ?? [],
    // The ABAC carriers: who owns the overlay and who it is shared with. Left
    // out of the projection, a shared overlay came back looking private — and
    // the surfaces that offer sharing seed from exactly these rows.
    owner_user_id: row.ownerUserId ?? null,
    visible_to_user_ids: row.visibleToUserIds ?? [],
    visible_to_team_ids: row.visibleToTeamIds ?? [],
    // Where it lives and whether the reader's × removes it — stamped by the
    // service, the same pair the REST row carries. Left absent (not guessed)
    // when the server did not say, so a host can tell "not yours" from "unknown".
    ...canonicalRung(row.rung) ? { rung: canonicalRung(row.rung) } : {},
    ...typeof row.detachable === "boolean" ? { detachable: row.detachable } : {}
    // Remaining CatalogAssignmentDTO fields (shadow keys) aren't part of the
    // lean overlay view and are left to the DTO's own defaults.
  };
}
function mapPrimaryPointer(row) {
  return {
    scope: "entity",
    scope_target_id: row.entityId,
    catalog_kind: "address",
    // A hidden choice names no address whatever the id field says.
    catalog_id: row.hidden ? null : row.catalogId ?? null,
    chain_id: row.chainId,
    // The SDL spells a rung in caps (`TENANT`); the REST row, in lower case.
    ...row.rung ? { rung: row.rung.toLowerCase() } : {},
    ...typeof row.version === "number" ? { version: row.version } : {},
    ...typeof row.hidden === "boolean" ? { hidden: row.hidden } : {}
  };
}
function mapEntityMapping(row) {
  if (!row) return null;
  return {
    chain: row.chainId,
    address: row.address,
    validity_periods: typeof row.validityPeriods === "string" ? JSON.parse(row.validityPeriods) : row.validityPeriods ?? [],
    entity_id: row.entityId,
    role: row.role ?? null,
    // `CORE` -> `core`: the schema declares its enums UPPER_SNAKE, the storage
    // vocabulary is lower-case, and the matrix compares against the latter.
    tier: toMemberTier(row.tier),
    confidence: row.confidence ?? void 0,
    confidence_status: row.confidenceStatus ?? void 0,
    valid_from: row.validFrom ?? null,
    valid_to: row.validTo ?? null,
    // Preserved as `undefined` when absent rather than coerced to a boolean:
    // "the read did not judge the window" is not "superseded", and a view that
    // conflated them would draw every current member as retired.
    active: row.active ?? void 0,
    is_human_approved: row.isHumanApproved,
    source: row.source ?? null,
    explanation: row.explanation ?? [],
    ...typeof row.inherited === "boolean" ? { inherited: row.inherited } : {}
  };
}
var ASSIGNMENT_FIELDS = "id scope scopeTargetId chainId catalogKind catalogId userId isHumanApproved createdAt confidence confidenceStatus explanation ownerUserId visibleToUserIds visibleToTeamIds rung detachable validFrom validTo validityPeriods";
var ADDRESS_ASSIGNMENTS_QUERY = (
  /* GraphQL */
  `
  query AddressAssignments($address: String!, $chainId: String!, $limit: Int, $cursor: String) {
    addressAssignments(address: $address, chainId: $chainId, limit: $limit, cursor: $cursor) {
      items { ${ASSIGNMENT_FIELDS} }
      count
      nextCursor
    }
  }
`
);
var ADDRESS_ENTITY_MAPPING_QUERY = (
  /* GraphQL */
  `
  query AddressEntityMappingQuery($address: String!, $chainId: String!, $asOf: Int) {
    addressEntityMapping(address: $address, chainId: $chainId, asOf: $asOf) {
      entityId chainId address role tier confidence confidenceStatus validFrom validTo validityPeriods active isHumanApproved source explanation
    }
  }
`
);
var ADDRESS_ENTITY_MAPPING_INHERITED_QUERY = (
  /* GraphQL */
  `
  query AddressEntityMappingInheritedQuery($address: String!, $chainId: String!, $asOf: Int) {
    addressEntityMapping(address: $address, chainId: $chainId, asOf: $asOf, inherited: true) {
      entityId chainId address role tier confidence confidenceStatus validFrom validTo validityPeriods active isHumanApproved source explanation inherited
    }
  }
`
);
var ASSIGN_ADDRESS_CATALOG_MUTATION = (
  /* GraphQL */
  `
  mutation AssignAddressCatalog($input: AssignCatalogInput!) {
    assignAddressCatalog(input: $input) { ${ASSIGNMENT_FIELDS} }
  }
`
);
var UNASSIGN_ADDRESS_CATALOG_MUTATION = (
  /* GraphQL */
  `
  mutation UnassignAddressCatalog($input: UnassignCatalogInput!) {
    unassignAddressCatalog(input: $input) { ok changed }
  }
`
);
var SET_ADDRESS_PRIMARY_MUTATION = (
  /* GraphQL */
  `
  mutation SetAddressPrimary($input: SetPrimaryInput!) {
    setAddressPrimary(input: $input) { entityId chainId catalogId rung version hidden }
  }
`
);
var CLEAR_ADDRESS_PRIMARY_MUTATION = (
  /* GraphQL */
  `
  mutation ClearAddressPrimary($input: ClearPrimaryInput!) {
    clearAddressPrimary(input: $input) { ok changed }
  }
`
);
var ENTITY_MAPPING_FIELDS = "entityId chainId address role tier confidence confidenceStatus validFrom validTo validityPeriods active isHumanApproved source explanation";
var PUT_ADDRESS_ENTITY_MAPPING_MUTATION = (
  /* GraphQL */
  `
  mutation PutAddressEntityMapping($input: PutEntityMappingInput!) {
    putAddressEntityMapping(input: $input) { ${ENTITY_MAPPING_FIELDS} }
  }
`
);
var DELETE_ADDRESS_ENTITY_MAPPING_MUTATION = (
  /* GraphQL */
  `
  mutation DeleteAddressEntityMapping($input: DeleteEntityMappingInput!) {
    deleteAddressEntityMapping(input: $input) { ok changed }
  }
`
);
var ENTITY_MEMBERSHIPS_QUERY = (
  /* GraphQL */
  `
  query EntityMemberships(
    $entityId: ID!
    $limit: Int
    $cursor: String
    $preferCore: Boolean
    $addressType: String
    $chainIds: [String!]
  ) {
    entityMemberships(
      entityId: $entityId
      limit: $limit
      cursor: $cursor
      preferCore: $preferCore
      addressType: $addressType
      chainIds: $chainIds
    ) {
      items { ${ENTITY_MAPPING_FIELDS} }
      count
      nextCursor
      primaryByChain
      memberTotal
      asOf
      lane
      truncated
    }
  }
`
);
var PUT_ADDRESS_ENTITY_MEMBERSHIPS_MUTATION = (
  /* GraphQL */
  `
  mutation PutAddressEntityMemberships($input: PutEntityMembershipsInput!) {
    putAddressEntityMemberships(input: $input) {
      count
      failed
      results {
        chainId
        address
        ok
        error
        mapping { ${ENTITY_MAPPING_FIELDS} }
      }
    }
  }
`
);
var ASSIGNMENTS_PAGE_BUDGET = 20;
async function graphqlListAssignments(client, args, opts) {
  const onePage = args.limit !== void 0 || args.cursor !== void 0;
  const out = [];
  let cursor = args.cursor;
  for (let page = 0; page < ASSIGNMENTS_PAGE_BUDGET; page += 1) {
    const data = await client.request(
      ADDRESS_ASSIGNMENTS_QUERY,
      { address: args.address, chainId: args.chainId, limit: args.limit, cursor },
      opts
    );
    const result = data.addressAssignments;
    for (const item of result?.items ?? []) out.push(mapAssignment(item));
    cursor = result?.nextCursor ?? void 0;
    if (onePage || !cursor) return out;
  }
  throw new GraphQLRequestError(
    `addressAssignments: more than ${ASSIGNMENTS_PAGE_BUDGET} pages for ${args.chainId}:${args.address}`
  );
}
function entityMembershipsUnroutable(query) {
  if (query.chains?.length) return "headline chain coverage (`chains`)";
  return null;
}
async function graphqlAttachAssignment(client, args, opts) {
  const { input } = args;
  const data = await client.request(
    ASSIGN_ADDRESS_CATALOG_MUTATION,
    {
      input: omitUndefined({
        address: args.address,
        chainId: args.chainId,
        kind: toCatalogKindEnum(input.catalog_kind),
        catalogId: input.catalog_id,
        userScoped: input.user_scoped ?? true,
        // `CatalogRung` — the SDL's enum spelling of a shared rung.
        rung: input.rung ? input.rung.toUpperCase() : void 0,
        visibleToUserIds: input.visible_to_user_ids,
        visibleToTeamIds: input.visible_to_team_ids,
        confidence: input.confidence,
        reason: input.reason,
        evidenceUrl: input.evidence_url,
        validFrom: input.valid_from,
        validTo: input.valid_to,
        validityPeriods: input.validity_periods === void 0 ? void 0 : JSON.stringify(input.validity_periods)
      })
    },
    opts
  );
  return mapAssignment(data.assignAddressCatalog);
}
function omitUndefined(input) {
  const out = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== void 0) out[key] = value;
  }
  return out;
}
async function graphqlDetachAssignment(client, args, opts) {
  await client.request(
    UNASSIGN_ADDRESS_CATALOG_MUTATION,
    {
      input: omitUndefined({
        address: args.address,
        chainId: args.chainId,
        kind: toCatalogKindEnum(args.input.catalog_kind),
        catalogId: args.input.catalog_id,
        userScoped: args.input.user_scoped,
        rung: args.input.rung ? args.input.rung.toUpperCase() : void 0
      })
    },
    opts
  );
}
async function graphqlSetPrimary(client, args, opts) {
  const data = await client.request(
    SET_ADDRESS_PRIMARY_MUTATION,
    {
      input: omitUndefined({
        entityId: args.entityId,
        address: args.noAddress ? null : args.address,
        chainId: args.chainId,
        // `CatalogRung` — the SDL's enum spelling.
        rung: args.rung ? args.rung.toUpperCase() : void 0,
        expectedVersion: args.expectedVersion ?? void 0
      })
    },
    opts
  );
  return mapPrimaryPointer(data.setAddressPrimary);
}
async function graphqlClearPrimary(client, args, opts) {
  await client.request(
    CLEAR_ADDRESS_PRIMARY_MUTATION,
    {
      input: omitUndefined({
        entityId: args.entityId,
        chainId: args.chainId,
        rung: args.rung ? args.rung.toUpperCase() : void 0
      })
    },
    opts
  );
}
async function graphqlGetEntityMapping(client, args, opts) {
  const data = await client.request(
    args.inherited ? ADDRESS_ENTITY_MAPPING_INHERITED_QUERY : ADDRESS_ENTITY_MAPPING_QUERY,
    { address: args.address, chainId: args.chainId, asOf: args.asOf },
    opts
  );
  return mapEntityMapping(data.addressEntityMapping);
}
async function graphqlPutEntityMapping(client, args, opts) {
  const { input } = args;
  const data = await client.request(
    PUT_ADDRESS_ENTITY_MAPPING_MUTATION,
    {
      input: {
        address: args.address,
        chainId: args.chainId,
        entityId: input.entity_id,
        role: input.role,
        tier: toTierEnum(input.tier),
        confidence: input.confidence,
        validFrom: input.valid_from,
        validTo: input.valid_to,
        validityPeriods: input.validity_periods === void 0 ? void 0 : JSON.stringify(input.validity_periods)
      }
    },
    opts
  );
  return mapEntityMapping(data.putAddressEntityMapping);
}
async function graphqlDeleteEntityMapping(client, args, opts) {
  const data = await client.request(
    DELETE_ADDRESS_ENTITY_MAPPING_MUTATION,
    { input: { address: args.address, chainId: args.chainId, entityId: args.entityId } },
    opts
  );
  return data.deleteAddressEntityMapping?.changed ?? 0;
}
async function graphqlListEntityMemberships(client, args, opts) {
  const data = await client.request(
    ENTITY_MEMBERSHIPS_QUERY,
    {
      entityId: args.entity_id,
      limit: args.limit,
      cursor: args.cursor,
      preferCore: args.prefer_core,
      addressType: args.address_type,
      // `chains` is NOT sent here: on this query it scopes the headline pointer,
      // and GraphQL derives that coverage itself. This one narrows the rows.
      chainIds: args.chain_ids?.length ? [...args.chain_ids] : void 0
    },
    opts
  );
  const page = data.entityMemberships;
  const mappings = (page?.items ?? []).map(mapEntityMapping).filter((m) => m != null);
  return {
    mappings,
    next_cursor: page?.nextCursor ?? null,
    count: page?.count ?? mappings.length,
    primary_by_chain: mapPrimaryByChain(page?.primaryByChain),
    // `null` and absent both mean "nobody has counted this entity", which the
    // caller must render as unknown rather than as zero.
    member_total: page?.memberTotal ?? null,
    lane: toLane(page?.lane),
    truncated: page?.truncated ?? false
  };
}
function toLane(value) {
  return String(value ?? "").toLowerCase() === "core" ? "core" : "all";
}
function mapPrimaryByChain(raw) {
  if (raw == null) return {};
  let source = raw;
  if (typeof raw === "string") {
    try {
      source = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof source !== "object" || source === null || Array.isArray(source)) return {};
  const out = {};
  for (const [chain, address] of Object.entries(source)) {
    if (chain && typeof address === "string" && address) out[chain] = address;
  }
  return out;
}
async function graphqlPutEntityMemberships(client, args, opts) {
  const data = await client.request(
    PUT_ADDRESS_ENTITY_MEMBERSHIPS_MUTATION,
    {
      input: omitUndefined({
        entityId: args.entity_id,
        role: args.role,
        tier: toTierEnum(args.tier),
        confidence: args.confidence,
        members: args.members.map(
          (m) => omitUndefined({
            address: m.address,
            chainId: m.chain,
            role: m.role,
            tier: toTierEnum(m.tier),
            confidence: m.confidence,
            validFrom: m.valid_from,
            validTo: m.valid_to,
            validityPeriods: m.validity_periods === void 0 ? void 0 : JSON.stringify(m.validity_periods)
          })
        )
      })
    },
    opts
  );
  const out = data.putAddressEntityMemberships;
  const results = (out?.results ?? []).map((r) => ({
    chain: r.chainId,
    address: r.address,
    ok: r.ok,
    mapping: mapEntityMapping(r.mapping),
    error: r.error ?? null
  }));
  return {
    results,
    count: out?.count ?? results.filter((r) => r.ok).length,
    failed: out?.failed ?? results.filter((r) => !r.ok).length,
    mappings: results.map((r) => r.mapping).filter((m) => m != null)
  };
}

export { ADDRESSES_QUERY, ADDRESS_ASSIGNMENTS_QUERY, ADDRESS_CROSS_CHAIN_QUERY, ADDRESS_DETAIL_QUERY, ADDRESS_ENTITY_MAPPING_INHERITED_QUERY, ADDRESS_ENTITY_MAPPING_QUERY, ADDRESS_OVERLAYS_QUERY, ADDRESS_ROW_FIELDS, ADDRESS_SCOPES, ASSIGNMENTS_PAGE_BUDGET, ASSIGN_ADDRESS_CATALOG_MUTATION, CATALOG_ITEMS_QUERY, CATALOG_ITEM_FIELDS, CATALOG_ITEM_QUERY, CATALOG_RELATED_QUERY, CLEAR_ADDRESS_PRIMARY_MUTATION, DELETE_ADDRESS_ENTITY_MAPPING_MUTATION, EMPTY_BATCH_OVERLAYS, ENTITY_MEMBERSHIPS_QUERY, GRAPHQL_BATCH_REF_LIMIT, GRAPHQL_CATALOG_ID_LIMIT, GraphQLClient, GraphQLRequestError, LATEST_TRANSACTIONS_QUERY, PUT_ADDRESS_ENTITY_MAPPING_MUTATION, PUT_ADDRESS_ENTITY_MEMBERSHIPS_MUTATION, RELATED_SEED_KINDS, REQUEST_TRANSACTION_MUTATION, RESOLVED_ENTITY_FIELDS, RESOLVED_HIT_CORE_FIELDS, RESOLVED_HIT_FIELDS, SET_ADDRESS_PRIMARY_MUTATION, TRANSACTION_HEADER_QUERY, UNASSIGN_ADDRESS_CATALOG_MUTATION, addressBatchRequest, addressCrossChainIncomplete, addressListUnroutable, boundOverlayRefs, catalogBlockToResolvedOverlays, catalogItemsByIdRequest, catalogKindToGraphql, catalogListUnroutable, dedupeRefs, entityMembershipsUnroutable, fetchAddressOverlays, fetchAddressOverlaysBatch, fetchCatalogItem, fetchCatalogItems, fetchCatalogRelated, fetchLatestTransactions, fetchTransactionHeader, graphqlAttachAssignment, graphqlClearPrimary, graphqlDeleteEntityMapping, graphqlDetachAssignment, graphqlFetchAddressCrossChain, graphqlFetchAddressDetail, graphqlFetchCatalogItemsByIds, graphqlGetEntityMapping, graphqlListAddresses, graphqlListAddressesWithOverlays, graphqlListAssignments, graphqlListEntityMemberships, graphqlListMode, graphqlPutEntityMapping, graphqlPutEntityMemberships, graphqlRequestTransaction, graphqlResolveAddressesBatch, graphqlSetPrimary, indexBatchEntries, indexPreparedEntries, isEmptyOverlays, isGraphQLRequestError, isOpaqueRequestId, isOverlayAnswerConfirmed, isSavedAddressRow, lookupAddressRow, lookupOverlays, mapAddressCrossChain, mapAddressDetail, mapAddressListOverlays, mapAddressOverlays, mapAddressPage, mapAddressRow, mapAddressRowScope, mapAssignment, mapBundle, mapCatalogItemToRow, mapCatalogItems, mapCrossChainActivity, mapEntity, mapEntityMapping, mapLatestTransactions, mapPrimaryPointer, mapRelatedGraph, mapTransactionHeader, mapWireHit, overlayRefKey, prepareBatchEntry, refsCacheKey, relatedChildren, relatedOfKind, resolveAddressAcrossChains, resolvedBundleFields, toAddressScope, toCatalogKindEnum, toResolvedOverlays };
//# sourceMappingURL=chunk-362OWVT5.js.map
//# sourceMappingURL=chunk-362OWVT5.js.map